import React from "react";
import { createPortal } from "react-dom";

function rozdelObsahNaRiadky(obsah) {
  return String(obsah || "")
    .split(/\r\n|\r|\n/)
    .map((riadok) => riadok.trimEnd());
}

const ODSADENIE = 10;
const OKRAJ_VIEWPORTU = 12;
const MOBIL_BREAKPOINT = 700;

function ziskajPreferovanuPoziciu(triggerRect, rozmeryPanelu, pozicia) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const miestoHore = triggerRect.top;
  const miestoDole = viewportHeight - triggerRect.bottom;
  const miestoVlavo = triggerRect.left;
  const miestoVpravo = viewportWidth - triggerRect.right;

  if (pozicia === "bottom") {
    if (miestoDole >= rozmeryPanelu.height + ODSADENIE) return "bottom";
    if (miestoHore >= rozmeryPanelu.height + ODSADENIE) return "top";
    return "bottom";
  }

  if (pozicia === "left") {
    if (miestoVlavo >= rozmeryPanelu.width + ODSADENIE) return "left";
    if (miestoVpravo >= rozmeryPanelu.width + ODSADENIE) return "right";
    return "bottom";
  }

  if (pozicia === "right") {
    if (miestoVpravo >= rozmeryPanelu.width + ODSADENIE) return "right";
    if (miestoVlavo >= rozmeryPanelu.width + ODSADENIE) return "left";
    return "bottom";
  }

  if (miestoHore >= rozmeryPanelu.height + ODSADENIE) return "top";
  if (miestoDole >= rozmeryPanelu.height + ODSADENIE) return "bottom";
  return "bottom";
}

function vypocitajPoziciu(triggerRect, rozmeryPanelu, pozicia) {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top = 0;
  let left = 0;

  if (pozicia === "top") {
    top = triggerRect.top - rozmeryPanelu.height - ODSADENIE;
    left = triggerRect.left + (triggerRect.width / 2) - (rozmeryPanelu.width / 2);
  } else if (pozicia === "bottom") {
    top = triggerRect.bottom + ODSADENIE;
    left = triggerRect.left + (triggerRect.width / 2) - (rozmeryPanelu.width / 2);
  } else if (pozicia === "left") {
    top = triggerRect.top + (triggerRect.height / 2) - (rozmeryPanelu.height / 2);
    left = triggerRect.left - rozmeryPanelu.width - ODSADENIE;
  } else {
    top = triggerRect.top + (triggerRect.height / 2) - (rozmeryPanelu.height / 2);
    left = triggerRect.right + ODSADENIE;
  }

  const maxLeft = viewportWidth - rozmeryPanelu.width - OKRAJ_VIEWPORTU;
  const maxTop = viewportHeight - rozmeryPanelu.height - OKRAJ_VIEWPORTU;

  return {
    top: Math.min(Math.max(OKRAJ_VIEWPORTU, top), Math.max(OKRAJ_VIEWPORTU, maxTop)),
    left: Math.min(Math.max(OKRAJ_VIEWPORTU, left), Math.max(OKRAJ_VIEWPORTU, maxLeft)),
  };
}

export default function HintPopover({
  obsah,
  children,
  ariaLabel = "Zobraziť detail",
  pozicia = "top",
  wrapperClassName = "",
  triggerClassName = "",
  panelClassName = "",
  triggerMode = "hover",
}) {
  const [jeOtvoreny, setJeOtvoreny] = React.useState(false);
  const [stylPanelu, setStylPanelu] = React.useState(null);
  const [realnaPozicia, setRealnaPozicia] = React.useState(pozicia);

  const wrapperRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const panelId = React.useId();

  const jeTextovyObsah =
  typeof obsah === "string";

  const maObsah = jeTextovyObsah
    ? obsah.trim().length > 0
    : React.isValidElement(obsah);

  const obsahPrePoziciu =
    jeTextovyObsah
      ? obsah
      : null;

  const jeKlikaciRezim = triggerMode === "click";

  const podporujeHover = React.useMemo(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }, []);

  const jeMobilnyViewport = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= MOBIL_BREAKPOINT;
  }, []);

  const aktualizujPoziciu = React.useCallback(() => {
    if (!jeOtvoreny || !triggerRef.current || !panelRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const panelRect = panelRef.current.getBoundingClientRect();

    const rozmeryPanelu = {
      width: panelRect.width,
      height: panelRect.height,
    };

    const novaPozicia = ziskajPreferovanuPoziciu(triggerRect, rozmeryPanelu, jeMobilnyViewport ? "bottom" : pozicia);
    const novaPoloha = vypocitajPoziciu(triggerRect, rozmeryPanelu, novaPozicia);

    setRealnaPozicia(novaPozicia);
    setStylPanelu({
      top: `${novaPoloha.top}px`,
      left: `${novaPoloha.left}px`,
    });
  }, [jeOtvoreny, pozicia, jeMobilnyViewport]);

  React.useLayoutEffect(() => {
    aktualizujPoziciu();
  }, [
    aktualizujPoziciu,
    obsahPrePoziciu,
  ]);

  React.useEffect(() => {
    if (!jeOtvoreny) return;

    const handlePointerDown = (event) => {
      const klikVoWrapperi = wrapperRef.current?.contains(event.target);
      const klikVPaneli = panelRef.current?.contains(event.target);

      if (!klikVoWrapperi && !klikVPaneli) {
        setJeOtvoreny(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setJeOtvoreny(false);
      }
    };

    const handleReposition = () => {
      aktualizujPoziciu();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [jeOtvoreny, aktualizujPoziciu]);

  if (!maObsah) {
    return children;
  }

  const otvor = () => setJeOtvoreny(true);
  const zatvor = () => setJeOtvoreny(false);
  const prepni = () => setJeOtvoreny((prev) => !prev);

  const handleMouseEnter = () => {
    if (!jeKlikaciRezim && podporujeHover) otvor();
  };

  const handleMouseLeave = () => {
    if (!jeKlikaciRezim && podporujeHover) zatvor();
  };

  const handleFocus = () => {
    if (!jeKlikaciRezim) otvor();
  };

  const handleBlur = (event) => {
    if (jeKlikaciRezim) return;

    const relatedTarget = event.relatedTarget;
    if (wrapperRef.current?.contains(relatedTarget)) return;
    if (panelRef.current?.contains(relatedTarget)) return;
    zatvor();
  };

  const handleClick = () => {
    if (jeKlikaciRezim || !podporujeHover) {
      prepni();
    }
  };

  const riadky = jeTextovyObsah
    ? rozdelObsahNaRiadky(obsah)
    : [];

  return (
    <>
      <span
        ref={wrapperRef}
        className={`hint-popover hint-popover--${pozicia} ${wrapperClassName}`.trim()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          ref={triggerRef}
          type="button"
          className={`hint-trigger ${triggerClassName}`.trim()}
          aria-label={ariaLabel}
          aria-expanded={jeOtvoreny}
          aria-describedby={
            jeOtvoreny &&
            jeTextovyObsah
              ? panelId
              : undefined
          }
          aria-controls={
            jeOtvoreny &&
            !jeTextovyObsah
              ? panelId
              : undefined
          }
          onClick={handleClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {children}
        </button>
      </span>

      {jeOtvoreny && typeof document !== "undefined" && createPortal(
        <span
          ref={panelRef}
          id={panelId}
          role={
            jeTextovyObsah
              ? "tooltip"
              : "dialog"
          }
          aria-label={
            !jeTextovyObsah
              ? ariaLabel
              : undefined
          }
          className={`hint-popover__panel hint-popover__panel--portal hint-popover__panel--${realnaPozicia} ${panelClassName}`.trim()}
          style={stylPanelu || undefined}
        >
          {jeTextovyObsah
            ? riadky.map(
                (riadok, index) => (
                  <span
                    key={`${panelId}-${index}`}
                    className={`hint-popover__line${
                      riadok === ""
                        ? " hint-popover__line--spacer"
                        : ""
                    }`}
                  >
                    {riadok === ""
                      ? "\u00A0"
                      : riadok}
                  </span>
                )
              )
            : obsah}
        </span>,
        document.body
      )}
    </>
  );
}
